import React, { useState } from 'react';
import { Save, Calendar, ChevronDown, ChevronUp, Edit2, Trash2 } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { toast } from 'react-hot-toast';

interface CommentEditorProps {
  submissionId: string;
  comments: any;
  onUpdate: () => void;
}

interface Comment {
  content: string;
  index: number;
  date: string;
}

export const CommentEditor: React.FC<CommentEditorProps> = ({ submissionId, comments, onUpdate }) => {
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const commentsList = React.useMemo(() => {
    const list: Comment[] = [];
    for (let i = 1; i <= 10; i++) {
      const key = `comment_${i}`;
      const dateKey = `comment_${i}_date`;
      if (comments[key] && comments[key].trim() !== '') {
        list.push({
          content: comments[key],
          index: i,
          date: comments[dateKey] || ''
        });
      }
    }
    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [comments]);

  const handleSave = async () => {
    if (!editingComment) return;

    if (!editingComment.content.trim()) {
      toast.error('Le commentaire ne peut pas être vide');
      return;
    }

    setIsSaving(true);

    try {
      const response = await fetch('/api/edit-comment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submissionId,
          index: editingComment.index,
          content: editingComment.content
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur serveur');
      }

      toast.success(`Commentaire ${editingComment.index} mis à jour`);
      setEditingComment(null);
      onUpdate();
    } catch (error) {
      console.error('Erreur mise à jour commentaire:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (comment: Comment, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) {
      return;
    }

    try {
      const response = await fetch('/api/edit-comment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submissionId,
          index: comment.index,
          content: ''
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur serveur');
      }

      toast.success('Commentaire supprimé');
      onUpdate();
    } catch (error) {
      console.error('Erreur suppression commentaire:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (editingComment) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Modifier le commentaire {editingComment.index}
          </h3>
          <button
            onClick={() => setEditingComment(null)}
            className="text-gray-500 hover:text-gray-700"
          >
            Annuler
          </button>
        </div>
        
        <RichTextEditor
          content={editingComment.content}
          onChange={(value) => setEditingComment({ ...editingComment, content: value })}
        />

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border shadow-sm">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
        >
          <span className="font-medium text-gray-900">
            Commentaires ({commentsList.length})
          </span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </button>

        <div 
          className={`overflow-hidden transition-all duration-300 ${
            isExpanded ? 'max-h-[400px]' : 'max-h-0'
          }`}
        >
          <div className="border-t overflow-y-auto max-h-[400px]">
            {commentsList.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Aucun commentaire</p>
            ) : (
              <div className="divide-y">
                {commentsList.map((comment) => (
                  <div
                    key={comment.index}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-2" />
                        {formatDate(comment.date)}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingComment(comment);
                          }}
                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDelete(comment, e)}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div 
                      className="prose max-w-none line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: comment.content }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentEditor;